import React from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { filmData } from '../data/mockData';
import { Link } from 'react-router-dom';
import { ExternalLink, Heart, BookOpen, Star, User } from 'lucide-react';

const FilmPage = () => {
  const featureIcons = [User, Heart, Star, BookOpen];

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Navbar />
      
      {/* Video Section */}
      <section className="pt-24 pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="aspect-video rounded-2xl overflow-hidden bg-black">
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${filmData.youtubeId}`}
              title="COPD Highly Illogical: Remembering Leonard Nimoy Trailer"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
          <p className="text-center text-gray-500 text-sm mt-4">Watch the Official Trailer</p>
        </div>
      </section>

      {/* Film Info Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">
            Documentary Film
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mt-4 mb-6">
            {filmData.title}
          </h1>
          <p className="text-gray-400 text-lg leading-relaxed mb-8">
            {filmData.description}
          </p>
          <div className="flex items-center justify-center gap-6 text-gray-500 text-sm mb-8">
            <span>{filmData.type}</span>
            <span className="w-1 h-1 bg-gray-500 rounded-full" />
            <span>{filmData.year}</span>
          </div>
          <a
            href={filmData.watchLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all hover:scale-105"
          >
            Watch the Film
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* About Documentary Section */}
      <section className="py-24 bg-[#0f0f12]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">
              About the Documentary
            </span>
            <h2 className="text-4xl font-bold text-white mt-4">
              A Loving Tribute
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filmData.features.map((feature, index) => {
              const IconComponent = featureIcons[index];
              return (
                <div
                  key={index}
                  className="bg-[#141416] border border-gray-800/50 rounded-2xl p-8"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-amber-500/10 rounded-xl">
                      <IconComponent className="w-6 h-6 text-amber-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-400 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 bg-[#0a0a0b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-amber-500 text-6xl font-serif mb-6">"</div>
          <blockquote className="text-xl md:text-2xl font-light text-white leading-relaxed mb-8">
            {filmData.quote}
          </blockquote>
          <p className="text-amber-500 text-lg">
            — <span className="italic">Leonard Nimoy</span>, on his COPD diagnosis
          </p>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-24 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">
            In Partnership With
          </span>
          <h2 className="text-3xl font-bold text-white mt-4 mb-12">
            Health Organizations
          </h2>
          <p className="text-gray-400 mb-8">
            The documentary has been developed in partnership with leading health organizations to maximize its educational impact.
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            {filmData.partners.map((partner, index) => (
              <div
                key={index}
                className="px-8 py-4 bg-[#1a1a1c] border border-gray-800/50 rounded-xl"
              >
                <p className="text-white font-semibold">{partner}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0a0a0b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Support the Foundation
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Help us continue Leonard Nimoy's legacy and spread awareness about COPD.
          </p>
          <Link
            to="/donate"
            className="inline-flex items-center justify-center px-10 py-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all hover:scale-105 text-lg"
          >
            Make a Donation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FilmPage;
